import { useEffect, useLayoutEffect, useRef } from 'react'

import { AstraDialog, isShortcutPressed } from '~/astra'
import { trackUserEvent } from '~/utils/analytics'

import { useCommandPalette } from './CommandPaletteContext'
import { CommandPalette } from './index'
import { useConfirmAction } from './useConfirmAction'
import { useInitializeCommands } from './useInitializeCommands'

export const COMMAND_PALETTE_SHORTCUT = { keyboardKey: 'K', hasMeta: true } as const

const CommandPaletteWithCommands = ({ onClose }: { onClose: () => void }) => {
    const { confirm, confirmDialog } = useConfirmAction()
    const commands = useInitializeCommands({ confirm })

    return (
        <>
            <CommandPalette commands={commands} onClose={onClose} />
            {confirmDialog}
        </>
    )
}

export const DefaultCommandPalette = () => {
    const { isOpen, setIsOpen } = useCommandPalette()
    const isOpenRef = useRef(isOpen)

    useLayoutEffect(() => {
        isOpenRef.current = isOpen
    }, [isOpen])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) {
                return
            }

            // Cmd+K or Ctrl+K to toggle command palette
            if (isShortcutPressed(event, COMMAND_PALETTE_SHORTCUT) || isShortcutPressed(event, { keyboardKey: 'K', hasCtrl: true })) {
                event.preventDefault()
                event.stopPropagation()
                const willOpen = !isOpenRef.current
                isOpenRef.current = willOpen
                setIsOpen(willOpen)
                if (willOpen) void trackUserEvent('COMMAND_PALETTE_OPENED', { action: 'keyboardShortcut' })
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [setIsOpen])

    return (
        <AstraDialog
            isOpen={isOpen}
            onCloseDialog={() => setIsOpen(false)}
            title="Kommandosenter"
            showHeader={false}
            showOverlay={false}
            noPadding
            align="top"
            className="w-[740px]"
        >
            <CommandPaletteWithCommands onClose={() => setIsOpen(false)} />
        </AstraDialog>
    )
}
